import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { AppProvider, useApp } from './lib/AppContext';
import AuthScreens, { ResetPasswordScreen } from './components/auth/AuthScreens';
import BottomNav from './components/shared/BottomNav';
import ErrorBoundary from './components/shared/ErrorBoundary';
import './styles/globals.css';

// Route-level code splitting — each page's code (and whatever libraries it
// alone pulls in, e.g. recharts for Reports, xlsx for the export button)
// only downloads when the user actually navigates there, instead of all 14
// shipping in the initial bundle regardless of which route is opened first.
const DashboardPage         = lazy(() => import('./pages/DashboardPage'));
const TransactionsPage      = lazy(() => import('./pages/TransactionsPage'));
const BalancePage           = lazy(() => import('./pages/BalancePage'));
const BudgetPage            = lazy(() => import('./pages/BudgetPage'));
const ReportsPage           = lazy(() => import('./pages/ReportsPage'));
const SettingsPage          = lazy(() => import('./pages/SettingsPage'));
const AssetsPage            = lazy(() => import('./pages/AssetsPage'));
const LiabilitiesPage       = lazy(() => import('./pages/LiabilitiesPage'));
const CategoriesPage        = lazy(() => import('./pages/CategoriesPage'));
const IncomeSourcesPage     = lazy(() => import('./pages/IncomeSourcesPage'));
const TagsSettingsPage      = lazy(() => import('./pages/TagsSettingsPage'));
const BackupPage            = lazy(() => import('./pages/BackupPage'));
const NewTransactionForm    = lazy(() => import('./components/transactions/NewTransactionForm'));
const TransactionDetailPage = lazy(() => import('./components/transactions/TransactionDetailPage'));

// Reports — one routed page per report (see src/pages/reports/), each with
// its own optional URL params for drill-down depth so the browser back
// button unwinds one level at a time everywhere, not just at the top.
const IncomeExpensesReport      = lazy(() => import('./pages/reports/IncomeExpensesReport'));
const AssetsLiabilitiesReport   = lazy(() => import('./pages/reports/AssetsLiabilitiesReport'));
const DebtBalanceReport         = lazy(() => import('./pages/reports/DebtBalanceReport'));
const CashFlowReport            = lazy(() => import('./pages/reports/CashFlowReport'));
const ProjectedBalanceReport    = lazy(() => import('./pages/reports/ProjectedBalanceReport'));
const NetWorthReport            = lazy(() => import('./pages/reports/NetWorthReport'));
const PropertyPerformanceReport = lazy(() => import('./pages/reports/PropertyPerformanceReport'));
const SharePerformanceReport    = lazy(() => import('./pages/reports/SharePerformanceReport'));
const ReportsTransactionsList   = lazy(() => import('./pages/reports/ReportsTransactionsList'));
const ReportsPlannedList        = lazy(() => import('./pages/reports/ReportsPlannedList'));
const TagsReport                = lazy(() => import('./pages/reports/TagsReport'));

// Wrapper reads defaultDate from URL search params and passes as prop
function NewTransactionFormWrapper() {
  const [sp] = useSearchParams();
  return <NewTransactionForm defaultDate={sp.get('defaultDate')} />;
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-base)' }}>
      <div style={{ width: 64, height: 64, borderRadius: 18, background: 'linear-gradient(135deg, var(--accent) 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, boxShadow: 'var(--shadow-accent)' }}>
        <span style={{ fontSize: '1.75rem' }}>💰</span>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading MyFinance…</p>
    </div>
  );
}

function DuplicateTransactionPage() {
  const { id } = useParams();
  return <NewTransactionForm duplicateId={id} />;
}

// Patterns that should hide the bottom nav (full-screen takeover pages)
const HIDE_NAV_PATTERNS = [
  '/settings/assets',
  '/settings/liabilities',
  '/settings/categories',
  '/settings/income',
  '/settings/tags',
  '/settings/backup',
];

function AppLayout() {
  // useLocation correctly re-renders on every route change (back button, programmatic nav, etc)
  // unlike reading window.location directly which does NOT trigger a re-render
  const location = useLocation();
  const path = location.pathname;

  const hideNav = HIDE_NAV_PATTERNS.some(p => path.startsWith(p)) ||
    (path.startsWith('/transactions/') && path !== '/transactions') ||
    // Hide only once actually drilled into a report (a segment beyond its
    // own base path, e.g. /reports/income-expenses/Groceries) — the hub and
    // each report's top level keep the nav bar, same as /reports itself.
    /^\/reports\/[^/]+\/.+/.test(path);

  return (
    <div className="app-layout">
      <div className="page-content">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/transactions/new" element={<NewTransactionFormWrapper />} />
            <Route path="/transactions/duplicate/:id" element={<DuplicateTransactionPage />} />
            <Route path="/transactions/:id" element={<TransactionDetailPage />} />
            <Route path="/balance" element={<BalancePage />} />
            <Route path="/budget" element={<BudgetPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/income-expenses/:categoryId?/:childId?" element={<IncomeExpensesReport />} />
            <Route path="/reports/assets-liabilities/:side?/:itemId?" element={<AssetsLiabilitiesReport />} />
            <Route path="/reports/debt-balance/:liabilityId?" element={<DebtBalanceReport />} />
            <Route path="/reports/cash-flow/:mode?/:periodKey?" element={<CashFlowReport />} />
            <Route path="/reports/projected/:month?" element={<ProjectedBalanceReport />} />
            <Route path="/reports/net-worth/:section?" element={<NetWorthReport />} />
            <Route path="/reports/property/:propertyId?/:metric?" element={<PropertyPerformanceReport />} />
            <Route path="/reports/share-performance/:assetId?" element={<SharePerformanceReport />} />
            <Route path="/reports/transactions" element={<ReportsTransactionsList />} />
            <Route path="/reports/planned" element={<ReportsPlannedList />} />
            <Route path="/reports/tags/:tagId?/:tagView?" element={<TagsReport />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/assets" element={<AssetsPage />} />
            <Route path="/settings/liabilities" element={<LiabilitiesPage />} />
            <Route path="/settings/categories" element={<CategoriesPage />} />
            <Route path="/settings/income" element={<IncomeSourcesPage />} />
            <Route path="/settings/tags" element={<TagsSettingsPage />} />
            <Route path="/settings/backup" element={<BackupPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      {!hideNav && <BottomNav />}
    </div>
  );
}

function AuthGuard() {
  const { session, authLoading, isPasswordRecovery, clearPasswordRecovery, syncReady, user } = useApp();
  const [forceShow, setForceShow] = useState(false);

  // Safety valve: never block the UI for more than 8 seconds, even on a
  // brand-new device with a poor connection. Whatever data has synced by
  // then renders immediately; the rest continues syncing silently.
  useEffect(() => {
    if (syncReady) { setForceShow(false); return; }
    const t = setTimeout(() => setForceShow(true), 8000);
    return () => clearTimeout(t);
  }, [syncReady]);

  if (authLoading) return <LoadingScreen />;

  // First launch on a new device — show sync progress while data downloads
  if (session && user && !syncReady && !forceShow) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:16, padding:32, background:'var(--bg-base)' }}>
        <span className="spinner" style={{ width:36, height:36 }} />
        <p style={{ fontWeight:700, fontSize:'1.125rem', color:'var(--text-primary)', textAlign:'center' }}>Setting up MyFinance…</p>
        <p style={{ fontSize:'0.875rem', color:'var(--text-muted)', textAlign:'center', maxWidth:280 }}>Downloading your data for the first time. This usually takes a few seconds and only happens once per device.</p>
      </div>
    );
  }
  // PASSWORD_RECOVERY: user clicked reset link — show set-new-password screen
  // even though they technically have a session at this point.
  if (isPasswordRecovery) {
    return <ResetPasswordScreen onDone={() => { clearPasswordRecovery(); }} />;
  }
  if (!session) return <AuthScreens />;
  return <AppLayout />;
}

// Resets the ErrorBoundary automatically whenever the route changes —
// so if one screen crashes, navigating away (e.g. tapping back) self-heals
// instead of requiring a full app reload.
function ErrorBoundaryWithRouteReset({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <ErrorBoundaryWithRouteReset>
          <AuthGuard />
        </ErrorBoundaryWithRouteReset>
      </AppProvider>
    </BrowserRouter>
  );
}
